import { getOrderById } from "UpdatedFeatures/api";
import logo from "../../assets/img/logo.svg";
import React, { useRef, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Puff } from "react-loader-spinner";

/**
 * Helpers centimes (évite les erreurs float)
 */
const toCents = (value) => {
    const n = Number(value ?? 0);
    return Math.round(n * 100);
};
const fromCents = (cents) => (Number(cents ?? 0) / 100).toFixed(2);

/**
 * Essaie de trouver un taux dans plusieurs champs possibles.
 */
const getTaxRateFromTaxObj = (taxObj) => {
    if (!taxObj) return null;
    const candidates = [
        taxObj.rate,
        taxObj.value,
        taxObj.percent,
        taxObj.percentage,
        taxObj.taux,
    ];
    const found = candidates.find((v) => v !== undefined && v !== null && v !== "");
    const rate = found !== undefined ? Number(found) : null;
    return Number.isFinite(rate) ? rate : null;
};

const Reciept = ({ open, close, order }) => {
    const componentRef = useRef();
    const [ticketInfo, setTicketInfo] = useState(null);

    // Prevent rendering before order exists
    const orderId = order?.order?.id;

    // Load order details
    const {
        data: orderDetails,
        isLoading: isLoadingOrder,
        isError: isErrorOrder,
    } = useQuery({
        queryKey: ["order", orderId],
        queryFn: () => getOrderById({ orderId }),
        enabled: !!orderId && open, // optionnel: n'appelle l'API que si modal ouverte
    });

    // Set ticket info
    useEffect(() => {
        setTicketInfo(orderDetails?.order_ticket || null);
    }, [orderDetails]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (componentRef.current && !componentRef.current.contains(event.target)) {
                close();
            }
        };
        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [open, close]);

    // Print handler
    const handlePrint = () => {
        const printContent = componentRef.current;
        if (!printContent) return;

        const originalClass = printContent.className;
        printContent.className += " print-only";
        window.print();
        printContent.className = originalClass;
    };

    const client = order?.order?.user;
    const clientCategoryIds = client?.subscription?.category_ids || [];

    const isSubscribed =
        ticketInfo?.isSubscribed === 1 ||
        ticketInfo?.isSubscribed === true ||
        order?.order?.includes_subscription === 1;

    // Match category to get correct price (retourne un number)
    const returnCorrectPrice = (product) => {
        const productCategoryIds = product?.category_product?.map((c) => c.categ_parent) || [];
        const selectedSubCategoryIds = ticketInfo?.subscription_category_ids || [];

        const hasMatchingCategory = productCategoryIds.some(
            (id) => clientCategoryIds.includes(id) || selectedSubCategoryIds.includes(id)
        );

        const price = hasMatchingCategory ? product?.priceLolo : product?.priceNonSubscriber;
        return Number(price ?? 0);
    };

    // Convert UTC → locale user timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localOrderDate = ticketInfo?.order_date
        ? new Date(ticketInfo.order_date).toLocaleString("fr-FR", {
            timeZone: userTimezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        })
        : "Date inconnue";

    /**
     * 1) mapping taxId -> rate%
     */
    const taxMetaById = useMemo(() => {
        const map = new Map();

        // A) depuis produits
        (ticketInfo?.products || []).forEach((p) => {
            const taxObj = p?.taxes?.[0];
            const taxId = taxObj?.id ?? null;
            const rate = getTaxRateFromTaxObj(taxObj);

            if (taxId != null && rate != null && !map.has(String(taxId))) {
                map.set(String(taxId), { id: String(taxId), rate });
            }
        });

        // B) fallback depuis tax_breakdown (clé = taux, data.id = taxId)
        if (ticketInfo?.tax_breakdown) {
            Object.entries(ticketInfo.tax_breakdown).forEach(([rateStr, data]) => {
                const taxId = data?.id;
                const rate = Number(rateStr);
                if (taxId != null && Number.isFinite(rate) && !map.has(String(taxId))) {
                    map.set(String(taxId), { id: String(taxId), rate });
                }
            });
        }

        return map;
    }, [ticketInfo]);

    /**
     * 2) TVA recalculée par code (produits + shipping + abonnement) en centimes
     */
    const computedTaxBreakdown = useMemo(() => {
        const resultByTaxId = new Map();

        const addLine = ({ baseCents, taxId, rate, forcedTaxCents }) => {
            if (taxId == null) return;
            const id = String(taxId);

            const r = rate != null ? Number(rate) : taxMetaById.get(id)?.rate ?? null;
            if (!Number.isFinite(r)) return;

            const taxCents =
                forcedTaxCents != null ? Number(forcedTaxCents) : Math.round((baseCents * r) / 100);

            const current = resultByTaxId.get(id) || {
                id,
                rate: r,
                baseCents: 0,
                taxCents: 0,
            };

            resultByTaxId.set(id, {
                id,
                rate: current.rate ?? r,
                baseCents: current.baseCents + baseCents,
                taxCents: current.taxCents + taxCents,
            });
        };

        // A) Produits
        (ticketInfo?.products || []).forEach((item) => {
            const taxObj = item?.taxes?.[0];
            const taxId = taxObj?.id ?? null;
            const rate = getTaxRateFromTaxObj(taxObj) ?? taxMetaById.get(String(taxId))?.rate;

            const unitPrice = returnCorrectPrice(item);
            const qty = Number(item?.quantity ?? 0);

            const lineBaseCents = toCents(unitPrice) * qty;

            addLine({ baseCents: lineBaseCents, taxId, rate });
        });

        // B) Shipping (a aussi un code taxe)
        const shippingBaseCents = toCents(ticketInfo?.shippingCost);

        // 👉 on affiche/compte shipping_tax_id si dispo, sinon shipping_id
        const shippingTaxId = ticketInfo?.shipping_tax_id ?? ticketInfo?.shipping_id ?? null;

        if (shippingBaseCents > 0 && shippingTaxId != null) {
            const rate = taxMetaById.get(String(shippingTaxId))?.rate ?? null;
            addLine({ baseCents: shippingBaseCents, taxId: shippingTaxId, rate });
        }

        // C) Abonnement (si inclus)
        if (isSubscribed) {
            const subTaxId = ticketInfo?.subscription_tax_id ?? null;

            // base HT affichée
            const subBaseHT =
                Number(ticketInfo?.subscription_amount ?? 0) - Number(ticketInfo?.subscription_tax ?? 0);
            const subBaseCents = toCents(subBaseHT);

            // si backend donne subscription_tax, on la force pour coller exactement
            const forcedTaxCents =
                ticketInfo?.subscription_tax != null ? toCents(ticketInfo.subscription_tax) : null;

            if (subBaseCents > 0 && subTaxId != null) {
                const rate = taxMetaById.get(String(subTaxId))?.rate ?? null;
                addLine({
                    baseCents: subBaseCents,
                    taxId: subTaxId,
                    rate,
                    forcedTaxCents,
                });
            }
        }

        return Array.from(resultByTaxId.values()).sort((a, b) => {
            if (a.rate !== b.rate) return a.rate - b.rate;
            return String(a.id).localeCompare(String(b.id));
        });
    }, [ticketInfo, taxMetaById, isSubscribed]); // OK

    if (!open) return null;

    if (isErrorOrder) {
        return (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-500 bg-opacity-75">
                <div className="bg-white p-6 rounded shadow text-red-600 font-semibold">
                    Erreur lors du chargement de la commande
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                aria-hidden="true"
                onClick={close}
            />

            {isLoadingOrder ? (
                <div className="flex justify-center mx-auto items-center min-h-screen">
                    <Puff visible height="150" width="150" color="#f9cd02" />
                </div>
            ) : (
                <div className="flex items-center justify-center min-h-screen p-4 text-center">
                    <div
                        ref={componentRef}
                        className="receipt-content bg-white pb-4 rounded-lg shadow-lg px-8 max-w-xl mx-auto relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* HEADER */}
                        <div className="flex items-center justify-between mb-8">
                            <img className="h-40 w-40" src={logo} alt="Logo" />
                            <div className="text-sm flex flex-col items-start text-gray-700 ml-20 font-bold">
                                <div>Email: contact@kdmarche.com</div>
                                <div>Site web: www.kdmarche.com</div>
                                <div>SIREN : 433 230 703</div>
                                <div>POS : {order?.order?.point_of_sale?.name}</div>
                                <div>
                                    Adresse : {order?.order?.point_of_sale?.address},{" "}
                                    {order?.order?.point_of_sale?.code_postal}{" "}
                                    {order?.order?.point_of_sale?.ville}
                                </div>
                            </div>
                        </div>

                        {/* ORDER DETAILS */}
                        <div className="text-gray-700">
                            <div className="font-bold text-lg mb-4 text-[--ExtraColor]">
                                FACTURE LOLO DRIVE
                            </div>
                            <div className="text-lg">
                                Date : <span className="font-semibold">{localOrderDate}</span>
                            </div>
                            <div className="text-lg">
                                Numéro de facture :{" "}
                                <span className="font-semibold">{ticketInfo?.ref}</span>
                            </div>
                        </div>

                        <div className="border-b-2 border-gray-300 pb-4 mb-8" />

                        {/* CLIENT */}
                        <div className="border-b-2 border-gray-300 pb-8 mb-8">
                            <h2 className="text-2xl font-bold mb-4">Facturé à :</h2>
                            {client ? (
                                <>
                                    <div className="text-gray-700 mb-2">
                                        {client.first_name} {client.last_name} -{" "}
                                        {ticketInfo?.includes_subscription || ticketInfo?.is_subscribed ? (
                                            <>
                                                {ticketInfo?.includes_subscription ? "Nouvel abonné" : "Abonné"}
                                                {orderDetails?.subscription?.label &&
                                                ` : ${orderDetails?.subscription?.label}`}
                                            </>
                                        ) : (
                                            "Non Abonné"
                                        )}
                                    </div>
                                    <div className="text-gray-700 mb-2">{client.address}</div>
                                    <div className="text-gray-700 mb-2">
                                        {client.city}, {client.country} {client.zip}
                                    </div>
                                    <div className="text-gray-700">{client.email}</div>
                                </>
                            ) : (
                                <div className="text-gray-700">Informations client non disponibles</div>
                            )}
                        </div>

                        {/* TABLE PRODUITS */}
                        <table className="w-full text-left mb-8">
                            <thead>
                            <tr>
                                <th className="py-2 px-4 font-bold text-gray-700">Nom</th>
                                <th className="py-2 px-4 font-bold text-gray-700">Code Taxe</th>
                                <th className="py-2 px-4 font-bold text-gray-700">Qté</th>
                                <th className="py-2 px-4 font-bold text-gray-700">Prix</th>
                                <th className="py-2 px-4 font-bold text-gray-700">Total</th>
                            </tr>
                            </thead>

                            <tbody>
                            {(ticketInfo?.products || []).map((item) => {
                                const taxId = item?.taxes?.[0]?.id ?? "";
                                const unitPrice = returnCorrectPrice(item);
                                const qty = Number(item?.quantity ?? 0);
                                const totalPrice = (unitPrice * qty).toFixed(2);

                                return (
                                    <tr key={item.id}>
                                        <td className="py-4 px-4">{item.title}</td>
                                        <td className="py-4 px-8">{taxId}</td>
                                        <td className="py-4 px-8">{qty}</td>
                                        <td className="py-4 px-4">€{unitPrice.toFixed(2)}</td>
                                        <td className="py-4 px-4">€{totalPrice}</td>
                                    </tr>
                                );
                            })}

                            {/* SHIPPING */}
                            <tr>
                                <td className="py-4 px-4">Frais d'expédition :</td>
                                <td className="py-4 px-8">
                                    {ticketInfo?.shipping_tax_id ?? ticketInfo?.shipping_id ?? ""}
                                </td>
                                <td></td>
                                <td className="py-4 px-4">
                                    €{Number(ticketInfo?.shippingCost ?? 0).toFixed(2)}
                                </td>
                                <td className="py-4 px-4">
                                    €{Number(ticketInfo?.shippingCost ?? 0).toFixed(2)}
                                </td>
                            </tr>

                            {/* SUBSCRIPTION */}
                            {isSubscribed && (
                                <tr>
                                    <td className="py-4 px-4">Abonnement :</td>
                                    <td className="py-4 px-8">{ticketInfo?.subscription_tax_id ?? ""}</td>
                                    <td></td>
                                    <td className="py-4 px-4">
                                        €
                                        {(
                                            Number(ticketInfo?.subscription_amount ?? 0) -
                                            Number(ticketInfo?.subscription_tax ?? 0)
                                        ).toFixed(2)}
                                    </td>
                                    <td className="py-4 px-4">
                                        €
                                        {(
                                            Number(ticketInfo?.subscription_amount ?? 0) -
                                            Number(ticketInfo?.subscription_tax ?? 0)
                                        ).toFixed(2)}
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>

                        {/* TAXES */}
                        <div className="font-semibold text-gray-700 mb-2">Détail des taxes :</div>

                        <div className="flex justify-center flex-col items-center mb-8">
                            {computedTaxBreakdown.length > 0 ? (
                                computedTaxBreakdown.map((row) => (
                                    <div
                                        key={row.id}
                                        className="flex border-l px-2 justify-between gap-5 text-sm text-gray-600 mb-1 w-full max-w-xs"
                                    >
                    <span>
                      TVA {Number(row.rate).toFixed(2)}% (Code: {row.id}) :
                    </span>
                                        <span>€{fromCents(row.taxCents)}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="text-sm text-gray-500">Aucune taxe</div>
                            )}
                        </div>

                        {/* FRAIS STOCK PLUS */}
                        <div className="flex justify-center mb-8">
                            <div className="mr-2 text-gray-700">Frais Stock Plus :</div>
                            <div className="text-gray-700">
                                €{Number(ticketInfo?.frais_stock_plus ?? 0).toFixed(2)}
                            </div>
                        </div>

                        {/* TOTAL */}
                        <div className="flex justify-center items-center mb-8">
                            <div className="mr-2 text-gray-700">Total :</div>
                            <div className="font-bold text-xl text-gray-700">
                                €{Number(ticketInfo?.total ?? 0).toFixed(2)}
                            </div>
                        </div>

                        <div className="text-center text-gray-700 mb-4 font-semibold">
                            Merci pour votre achat. À bientôt !
                        </div>

                        {/* BUTTONS */}
                        <div className="flex justify-between gap-x-3">
                            <button
                                type="button"
                                onClick={handlePrint}
                                className="print-hide bg-[#b269ae] text-white px-4 py-2 rounded-md font-semibold hover:opacity-50"
                            >
                                Imprimer
                            </button>
                            <button
                                type="button"
                                className="print-hide bg-gray-100 px-4 py-2 rounded-md font-semibold"
                                onClick={close}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reciept;
